export type TBlogSortOption = 'name-asc' | 'name-desc' | 'newest' | 'oldest';

export interface IBlogListSearchState {
  page: number;
  itemsPerPage: number;
  selectedTags: string[];
  sortBy: TBlogSortOption;
}

interface IBuildBlogListQueryOptions {
  page?: number;
  selectedTags?: string[];
  sortBy?: TBlogSortOption;
  itemsPerPage?: number;
}

const DEFAULT_ITEMS_PER_PAGE = 12;
const DEFAULT_PAGE = 1;
const DEFAULT_SORT_BY: TBlogSortOption = 'newest';

const VALID_SORT_OPTIONS = new Set<TBlogSortOption>(['name-asc', 'name-desc', 'newest', 'oldest']);

function normalizePositiveInteger(value: number | string | undefined, fallback: number) {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
}

function normalizeTagValues(values: string[]) {
  return Array.from(
    new Set(
      values
        .flatMap((value) => value.split(','))
        .map((value) => value.trim())
        .filter(Boolean)
    )
  );
}

function getFirstSearchParamValue(value?: string | string[]) {
  return Array.isArray(value) ? value[0] : value;
}

export function getDefaultItemsPerPage(itemsPerPage?: number | string) {
  return normalizePositiveInteger(itemsPerPage, DEFAULT_ITEMS_PER_PAGE);
}

export function getDefaultSortBy() {
  return DEFAULT_SORT_BY;
}

export function parseBlogListSearchParams(
  searchParams?: Record<string, string | string[] | undefined>,
  defaultItemsPerPage?: number | string
): IBlogListSearchState {
  const page = normalizePositiveInteger(getFirstSearchParamValue(searchParams?.page), DEFAULT_PAGE);
  const itemsPerPage = normalizePositiveInteger(
    getFirstSearchParamValue(searchParams?.limit),
    getDefaultItemsPerPage(defaultItemsPerPage)
  );
  const normalizedSortValue = getFirstSearchParamValue(searchParams?.sort);
  const sortBy =
    normalizedSortValue && VALID_SORT_OPTIONS.has(normalizedSortValue as TBlogSortOption)
      ? (normalizedSortValue as TBlogSortOption)
      : DEFAULT_SORT_BY;
  const rawTags = searchParams?.tag;
  const selectedTags = normalizeTagValues(Array.isArray(rawTags) ? rawTags : rawTags ? [rawTags] : []);

  return {
    page,
    itemsPerPage,
    selectedTags,
    sortBy,
  };
}

export function parseBlogListQueryString(queryString?: string, defaultItemsPerPage?: number | string) {
  const params = new URLSearchParams(queryString || '');
  const normalizedParams: Record<string, string | string[] | undefined> = {};

  params.forEach((value, key) => {
    const existingValue = normalizedParams[key];

    if (typeof existingValue === 'undefined') {
      normalizedParams[key] = value;
      return;
    }

    normalizedParams[key] = Array.isArray(existingValue) ? [...existingValue, value] : [existingValue, value];
  });

  const parsedState = parseBlogListSearchParams(normalizedParams, defaultItemsPerPage);

  return {
    page: normalizePositiveInteger(parsedState.page, DEFAULT_PAGE),
    itemsPerPage: normalizePositiveInteger(parsedState.itemsPerPage, getDefaultItemsPerPage(defaultItemsPerPage)),
    selectedTags: parsedState.selectedTags || [],
    sortBy: VALID_SORT_OPTIONS.has(parsedState.sortBy) ? parsedState.sortBy : DEFAULT_SORT_BY,
  };
}

export function buildBlogListQueryString(currentParams: URLSearchParams, updates: IBuildBlogListQueryOptions = {}) {
  const params = new URLSearchParams(currentParams.toString());

  if (updates.selectedTags) {
    params.delete('tag');
    const normalizedTags = normalizeTagValues(updates.selectedTags);

    if (normalizedTags.length) {
      params.set('tag', normalizedTags.join(','));
    }
  }

  if (typeof updates.sortBy !== 'undefined') {
    if (updates.sortBy === DEFAULT_SORT_BY) {
      params.delete('sort');
    } else {
      params.set('sort', updates.sortBy);
    }
  }

  if (typeof updates.page !== 'undefined') {
    if (updates.page <= DEFAULT_PAGE) {
      params.delete('page');
    } else {
      params.set('page', updates.page.toString());
    }
  }

  if (typeof updates.itemsPerPage !== 'undefined') {
    if (updates.itemsPerPage === DEFAULT_ITEMS_PER_PAGE) {
      params.delete('limit');
    } else {
      params.set('limit', updates.itemsPerPage.toString());
    }
  }

  return params.toString();
}
